import { motion as Motion } from 'framer-motion';
import { useAtlasProjection } from '../../components/map/AtlasProjectionContext';
import s from './MapNode.module.css';

/**
 * Single node overlay inside <MapAtlasFrame>. State-driven tap behavior is owned
 * by MapPage; this component just renders. `shake` is set briefly when a locked
 * node is tapped — framer-motion animates the wobble (per ADR-044, no CSS
 * keyframes). Position is projected from the node's source-image coordinate via
 * the atlas frame's projection context, so it lands on the painted platform at
 * every breakpoint.
 *
 * `node` shape: { node_key, src_x, src_y, kind, level }.
 */
export default function MapNode({ node, state, shake = false, onTap = () => {} }) {
  const isMilestone = node.kind === 'milestone';
  const { projectNode } = useAtlasProjection();
  const { x, y } = projectNode(node.src_x, node.src_y, 'center');

  const style = {
    '--node-x': `${x}px`,
    '--node-y': `${y}px`,
  };

  return (
    <Motion.button
      type="button"
      className={`${s.node} ${s[state] || ''} ${isMilestone ? s.milestone : ''}`}
      style={style}
      onClick={onTap}
      aria-label={`Level ${node.level} ${node.kind}`}
      data-node-level={node.level}
      data-node-type={node.kind}
      animate={shake ? { x: [0, -6, 6, -5, 5, -3, 3, 0] } : { x: 0 }}
      transition={shake ? { duration: 0.4, ease: 'easeInOut' } : { duration: 0 }}
    >
      {state === 'current' && (
        <Motion.span
          className={s.glow}
          aria-hidden="true"
          animate={{ scale: [1, 1.15, 1], opacity: [0.75, 1, 0.75] }}
          transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
        />
      )}

      <span className={s.ring} aria-hidden="true">
        {state === 'completed' && (
          <svg className={s.checkIcon} viewBox="0 0 16 16" fill="none">
            <path
              d="M3 8.5l3.5 3.5 6.5-7"
              stroke="var(--color-brand-teal, #2ab8a8)"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </span>
    </Motion.button>
  );
}
